import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from './ConfirmDialog';

/**
 * Etki önizlemesi (onay akışı kuralları, spec 2026-11-01 §4.5): bir kural / istisna değişikliği kaydedilmeden önce
 * "kimin onay akışı değişecek?" listesi. `impact` = sunucunun ApprovalImpactResponse'u
 * ({ changedCount, truncated, items: [{ employeeId, fullName, beforeFlowName, afterFlowName }] }); akış adı boşsa
 * kişi yönetici / İK yedeğine düşer.
 */
export default function ImpactPreviewDialog({ impact, onConfirm, onCancel, loading = false }) {
  const { t } = useTranslation();
  if (!impact) return null;
  const items = impact.items || [];
  const fallback = t('approvalFlow.fallbackRoute');

  return (
    <ConfirmDialog
      isOpen
      type="warning"
      title={t('approvalFlow.impactTitle')}
      message={impact.changedCount > 0
        ? t('approvalFlow.impactCount', { count: impact.changedCount })
        : t('approvalFlow.impactNone')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      loading={loading}
    >
      {items.length > 0 && (
        <List data-testid="impact-preview-list">
          {items.map((i) => (
            <Row key={i.employeeId}>
              <Name>{i.fullName}</Name>
              <Change>{`${i.beforeFlowName || fallback} → ${i.afterFlowName || fallback}`}</Change>
            </Row>
          ))}
        </List>
      )}
      {impact.truncated && <Note>{t('approvalFlow.impactTruncated', { count: items.length })}</Note>}
    </ConfirmDialog>
  );
}

/**
 * Önizlemeli eylem: `preview()` sunucudan etkiyi getirir; kimsenin akışı değişmiyorsa `action()` doğrudan çalışır,
 * değişiyorsa liste gösterilir ve eylem onayla çalışır. Önizleme alınamazsa (yetki / ağ — hata interceptor'da)
 * eylem ÇALIŞMAZ.
 */
export function useImpactPreview() {
  const [state, setState] = useState(null); // { impact, action }
  const [running, setRunning] = useState(false);

  const runWithPreview = useCallback(async (preview, action) => {
    const res = await preview();
    const impact = res?.data?.status === true ? res.data.data : null;
    if (!impact) return false;
    if (!impact.changedCount) {
      await action();
      return true;
    }
    setState({ impact, action });
    return false;
  }, []);

  const confirm = useCallback(async () => {
    if (!state) return;
    setRunning(true);
    try {
      await state.action();
    } finally {
      setRunning(false);
      setState(null);
    }
  }, [state]);

  const cancel = useCallback(() => setState(null), []);

  const dialog = state
    ? <ImpactPreviewDialog impact={state.impact} onConfirm={confirm} onCancel={cancel} loading={running} />
    : null;

  return { runWithPreview, dialog };
}

const List = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-bottom: 1px solid #F1F5F9;
  &:last-child { border-bottom: none; }
`;

const Name = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Change = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const Note = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #A0A8B4;
`;
